import { useId, useState } from 'react';
import { cn } from 'cn';
import { Button } from '@/components/ui/button';
import { describeAlignment, splitParagraphs } from './editorTypes';

/**
 * Paste a whole translation at once.
 *
 * The counts are shown **before** anything is applied, because the failure
 * this prevents is silent: a paste one paragraph short does not look wrong,
 * it just attaches every translation after the gap to the wrong passage. The
 * server refuses a mismatch too — this is the courtesy that lets the
 * translator fix it while they can still see their own text.
 */
export function PasteSheet({
  expected,
  onApply,
  onClose,
}: {
  expected: number;
  onApply: (text: string) => void;
  onClose: () => void;
}) {
  const [text, setText] = useState('');
  const fieldId = useId();
  const statusId = useId();

  const paragraphs = splitParagraphs(text);
  const alignment = describeAlignment(paragraphs.length, expected);
  const empty = paragraphs.length === 0;

  return (
    <div
      className="fixed inset-0 z-50 flex items-end justify-center bg-overlay p-0 sm:items-center sm:p-6"
      onClick={onClose}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby={`${fieldId}-title`}
        onClick={(event) => event.stopPropagation()}
        onKeyDown={(event) => {
          if (event.key === 'Escape') onClose();
        }}
        className="flex max-h-[90dvh] w-full max-w-[720px] flex-col gap-3 rounded-t-xl border border-border bg-card p-5 sm:rounded-xl"
      >
        <div>
          <h2 id={`${fieldId}-title`} className="text-base font-semibold">
            Paste the whole translation
          </h2>
          <p className="mt-1 text-sm text-muted-foreground">
            One paragraph per passage, separated by a blank line. The{' '}
            {expected === 1 ? 'single passage' : `${expected} passages`} that need a
            translation are the non-Qur’an ones — Qur’an is never translated here.
          </p>
        </div>

        <label htmlFor={fieldId} className="sr-only">
          Pasted translation
        </label>
        <textarea
          id={fieldId}
          autoFocus
          value={text}
          onChange={(event) => setText(event.target.value)}
          aria-describedby={statusId}
          rows={12}
          className="min-h-48 w-full resize-y rounded-md border border-border bg-background p-3 text-sm outline-none focus-visible:border-ring focus-visible:ring-3 focus-visible:ring-ring/50"
        />

        <p
          id={statusId}
          aria-live="polite"
          className={cn(
            'text-sm',
            empty
              ? 'text-muted-foreground'
              : alignment.ok
                ? 'text-success'
                : 'text-warning-foreground',
          )}
        >
          {empty ? 'Nothing pasted yet.' : alignment.message}
        </p>

        <div className="flex flex-wrap justify-end gap-2">
          <Button type="button" variant="ghost" size="lg" onClick={onClose}>
            Cancel
          </Button>
          <Button
            type="button"
            size="lg"
            // Allowed even when the counts disagree: the editor may want the
            // text in the fields so they can see WHERE it went wrong, which
            // is far easier to fix than a number in a warning.
            disabled={empty}
            onClick={() => onApply(text)}
          >
            {alignment.ok
              ? 'Fill the passages'
              : `Fill anyway — ${paragraphs.length} of ${expected}`}
          </Button>
        </div>
      </div>
    </div>
  );
}
